import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import Paper from "@material-ui/core/Paper";
import Tooltip from "@material-ui/core/Tooltip";
import { makeStyles } from "@material-ui/core/styles";
import { BackButton } from "../../../Utils/FormInputs/BackButton";
import TextField from "../../../Utils/FormInputs/TextField";
import { formatAmount } from "../../../Utils/formatNumbers";
import { formatDate } from "../../../Utils/ConvertDate";
import { CustomPagination } from "../../../Utils/Tables/CustomTable";
import { CustomSelect } from "../../../Utils/FormInputs/SelectInput";
import Form from "react-bootstrap/Form";
import Label from "../../../Utils/FormInputs/Label";
import CustomDialog from "../../../Utils/Dialog/CustomDialog";
import { usePrint } from "../../../Utils/Template/usePrint";
import { PaymentVoucherReceipt } from "../../Report/PaymentVouchers";
import axios from "axios";
import config from "../../../Helpers/config.json";

const useStyles = makeStyles((theme) => ({
    cleared: {
      color: "#28a745",
      fontWeight: 600,
      textTransform: "capitalize",
    },
    dueToday: {
      color: "#ff9800",
      fontWeight: 600,
      textTransform: "capitalize",
    },
    overdue: {
      color: "#f44336",
      fontWeight: 600,
      textTransform: "capitalize",
    },
    tableContainer: {
      overflowX: "auto",
      marginTop: theme.spacing(2),
      boxShadow: "0 3px 15px rgba(0,0,0,0.08)",
      borderRadius: theme.spacing(1),
    },
    table: {
      minWidth: 650,
      '& th': {
        backgroundColor: "#f0f2f5",
        fontWeight: 700,
        color: "#333",
        textAlign: "center",
      },
      '& td': {
        verticalAlign: "middle",
        textAlign: "center",
      },
      '& tr:hover': {
        backgroundColor: "#f9f9f9",
        transition: "0.2s",
      },
    },
    header: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1),
      marginBottom: theme.spacing(3),
      [theme.breakpoints.up('md')]: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
      },
    },
    searchInput: {
      maxWidth: 350,
      width: "100%",
    },
    actionBtn: {
      margin: theme.spacing(0.5, 0.5, 0.5, 0),
      padding: theme.spacing(0.5, 1.2),
      fontSize: "0.85rem",
      borderRadius: "4px",
      fontWeight: 500,
      transition: "all 0.2s ease-in-out",
      "&:hover": {
        transform: "scale(1.05)",
      },
    },
    btnView: {
      backgroundColor: "#1976d2",
      color: "#fff",
      "&:hover": {
        backgroundColor: "#115293",
      },
    },
    btnReceive: {
      backgroundColor: "#28a745",
      color: "#fff",
      "&:hover": {
        backgroundColor: "#1e7e34",
      },
    },
    btnRefund: {
      backgroundColor: "#ff9800",
      color: "#fff",
      "&:hover": {
        backgroundColor: "#e67e22",
      },
    },
    tooltip: {
      fontSize: "0.85rem",
    },
    noDataCell: {
      textAlign: "center",
      color: "#757575",
      fontStyle: "italic",
    },
  }));
  
const OutStandingInvoices = ({   actions,match }) => {
  const classes = useStyles();
  const { supplierId, supplierName } = match.params;

  const [invoices, setInvoices] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // --- Modal state ---
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [invoiceBalance, setInvoiceBalance] = useState(0);
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // --- Voucher dialog ---
  const [voucherDialogOpen, setVoucherDialogOpen] = useState(false);
  const [voucherData, setVoucherData] = useState(null);
  const { handlePrint, fontSize, componentRef } = usePrint("");


// --- Refund modal state ---
const [refundModalOpen, setRefundModalOpen] = useState(false);
const [refundAmount, setRefundAmount] = useState("");
const [refundDate, setRefundDate] = useState(new Date().toISOString().slice(0, 10));
const [refundAccounts, setRefundAccounts] = useState([]);
const [selectedRefundAccount, setSelectedRefundAccount] = useState(null);
const [refundNotes, setRefundNotes] = useState("");
const [refundSubmitted, setRefundSubmitted] = useState(false);


  // --- Fetch invoices ---
  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const res = await axios.post(
        `${config.epharmUrl}/Cmanufacturer/supplier_payables/single_supplier_details`,
        { supplier_id: supplierId, search },
        { params: { page, per_page: perPage } }
      );
      setInvoices(res.data.data);
      setTotalCount(res.data.total_count);
    } catch (err) {
      console.error("Error fetching invoices", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, [page, search]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // --- Open modal ---
  const handleReceivePayment = (invoice) => {
    setSelectedInvoice(invoice);
    setInvoiceBalance(invoice.balance);
    setPaymentAmount(invoice.balance); // default amount
    setSelectedAccount(null);
    setNotes("");
    setSubmitted(false);
    setModalOpen(true);

    // Fetch accounts
    axios
      .get(`${config.epharmUrl}/accounting/ledgers_under_category`)
      .then((res) => {
        const data = res.data?.data || [];
        const options = data.map((item) => ({ value: item.id, label: item.head_name }));
        setAccounts(options);
      })
      .catch((err) => console.error("Error fetching accounts", err));
  };

  // --- Submit payment ---
  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    setSubmitted(true);

    // if (!selectedAccount || !paymentAmount || !paymentDate) return;
    if (!selectedAccount || !paymentDate || !paymentAmount || Number(paymentAmount) <= 0) {
      return;
    }
    

    const data = {
      invoice_id: selectedInvoice?.id,
      supplier_id: supplierId,
      payable_id: selectedInvoice?.supplier_payment_id,
      amount_paid: paymentAmount,
      credit_ledger: selectedAccount.value,
      payment_date: paymentDate,
      notes,
    };

    try {
      setSubmitting(true);
      const res = await axios.post(
        `${config.epharmUrl}/Cmanufacturer/supplier_payables/payment`,
        data
      );

      // Extract voucher details
      const responseData = res.data?.data;
      if (responseData) {
        setVoucherData(responseData);
        setVoucherDialogOpen(true);
      }

      // Refresh table and close modal
      fetchInvoices();
      handleClearModal();
    } catch (err) {
      console.error("Error submitting payment", err);
    } finally {
      setSubmitting(false);
    }
  };

  // --- Clear modal state ---
  const handleClearModal = () => {
    setModalOpen(false);
    setSelectedInvoice(null);
    setPaymentAmount("");
    setInvoiceBalance(0);
    setSelectedAccount(null);
    setNotes("");
    setSubmitted(false);
  };
  const handleSubmitRefund = async (e) => {
    e.preventDefault();
    setRefundSubmitted(true);
  
    if (!refundAmount || Number(refundAmount) <= 0 || !selectedRefundAccount || !refundDate) {
      return;
    }
      
    const data = {
      payable_id: selectedInvoice?.supplier_payment_id,
      refund_amount: refundAmount,
      debit_ledger: selectedRefundAccount?.value,
      refund_date: refundDate,
      notes: refundNotes,
    };
  
    try {
      setSubmitting(true);
      const res = await axios.post(
        `${config.epharmUrl}/Cmanufacturer/supplier_payables/refund`,
        data
      );
      const result = res.data?.data;
      if (result) {
        console.log("Voucher ID:", res.data.data.voucher_id);
        setVoucherData(res.data?.data);

        setVoucherDialogOpen(true);
        // setVoucherData(result);
        // setVoucherDialogOpen(true);
        fetchInvoices();
        setRefundModalOpen(false);
      }
    } catch (err) {
      console.error("Refund failed", err);
    } finally {
      setSubmitting(false);
    }
  };
  

  const handleRefund = (invoice) => {
    console.log("Refund for invoice:", invoice);
    setSelectedInvoice(invoice);
    setRefundAmount(invoice.total_paid || "");
    setRefundDate(new Date().toISOString().slice(0, 10));
    setRefundNotes("");
    setSelectedRefundAccount(null);
    setRefundSubmitted(false);
    setRefundModalOpen(true);
  
    // Fetch accounts (reuse API)
    axios
      .get(`${config.epharmUrl}/accounting/ledgers_under_category`)
      .then((res) => {
        const data = res.data?.data || [];
        const options = data.map((item) => ({
          value: item.id,
          label: item.head_name,
        }));
        setRefundAccounts(options);
      })
      .catch((err) => console.error("Error fetching refund accounts", err));
  };
  
  

  return (
    <div className="outstanding-invoices">
      <div className="header mb-3">
        <BackButton text="Back to Suppliers" to="/creditors/supplier" />
        <h3>Outstanding Invoices for {supplierName}</h3>
        <TextField
          type="text"
          placeholder="Search by chalan, date, or amount"
          value={search}
          onChange={handleSearchChange}
          className="mb-3"
        />
      </div>

      {/* --- Payment Modal --- */}
      <CustomDialog
        open={modalOpen}
        handleClose={handleClearModal}
        title="Receive Payment"
        maxWidth="sm"
        isOverflow={false}
      >
        <form onSubmit={handleSubmitPayment} autoComplete="off">
          <Form.Group>
            <Label name="Invoice Number" />
            <TextField type="text" value={selectedInvoice?.chalan_no || ""} disabled />
          </Form.Group>

          <Form.Group>
            <Label name="Invoice Balance" />
            <TextField type="text" value={invoiceBalance} disabled />
          </Form.Group>

          <Form.Group>
            <Label name="Payment Amount" type />
            <TextField
              type="number"
              name="paymentAmount"
              value={paymentAmount}
              submitted={submitted}
              onChange={(e) => {
                const value = e.target.value;
                // Allow empty input so user can delete "0"
                if (value === "") {
                  setPaymentAmount("");
                } else {
                  // Only allow positive numbers (no negatives or text)
                  const num = Number(value);
                  if (!isNaN(num)) setPaymentAmount(num);
                }
              }}              
            />
            {submitted && (!paymentAmount || Number(paymentAmount) <= 0) && (
              <div className="help-block">Payment amount must be greater than zero</div>
            )}

          </Form.Group>

          <Form.Group>
            <Label name="Payment Date" type />
            <TextField
              type="date"
              name="paymentDate"
              value={paymentDate}
              submitted={submitted}
              onChange={(e) => setPaymentDate(e.target.value)}
            />
            {submitted && !paymentDate && (
              <div className="help-block">Payment date is required</div>
            )}
          </Form.Group>

          <Form.Group>
            <Label name="Payment Account" type />
            <CustomSelect
              value={selectedAccount}
              submitted={submitted}
              onChange={setSelectedAccount}
              options={accounts}
            />
            {submitted && !selectedAccount && (
              <div className="help-block">Payment account is required</div>
            )}
          </Form.Group>

          <Form.Group>
            <Label name="Notes" />
            <TextField
              type="text"
              name="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Enter payment details or notes"
            />
          </Form.Group>

          <div className="mt-3">
            <button type="submit" className="btn btn-sm pharmacy-info-btn me-2">
              {submitting ? "Submitting..." : "Submit Payment"}
            </button>
            <button
              type="button"
              className="btn btn-sm pharmacy-gray-btn"
              onClick={handleClearModal}
            >
              Cancel
            </button>
          </div>
        </form>
      </CustomDialog>
      {/* refund Modal     */}
      <CustomDialog
        open={refundModalOpen}
        handleClose={() => setRefundModalOpen(false)}
        title="Supplier Refund"
        maxWidth="sm"
        isOverflow={false}
      >
        <form onSubmit={handleSubmitRefund} autoComplete="off">
          <Form.Group>
            <Label name="Refund Amount" />
            <TextField
              type="number"
              value={refundAmount}
              submitted={refundSubmitted}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "") {
                  setRefundAmount("");
                } else {
                  const num = Number(value);
                  if (!isNaN(num)) setRefundAmount(num);
                }
              }}
              
            />
            {refundSubmitted && (!refundAmount || Number(refundAmount) <= 0) && (
              <div className="help-block">Refund amount must be greater than zero</div>
            )}

          </Form.Group>

          <Form.Group>
            <Label name="Refund Date" />
            <TextField
              type="date"
              value={refundDate}
              submitted={refundSubmitted}
              onChange={(e) => setRefundDate(e.target.value)}
            />
            {refundSubmitted && !refundDate && (
              <div className="help-block">Refund date is required</div>
            )}
          </Form.Group>

          <Form.Group>
            <Label name="Refund Account" />
            <CustomSelect
              value={selectedRefundAccount}
              onChange={setSelectedRefundAccount}
              options={refundAccounts}
            />
            {refundSubmitted && !selectedRefundAccount && (
              <div className="help-block">Refund account is required</div>
            )}
          </Form.Group>

          <Form.Group>
            <Label name="Notes" />
            <TextField
              type="text"
              value={refundNotes}
              onChange={(e) => setRefundNotes(e.target.value)}
              placeholder="Enter refund reason or notes"
            />
          </Form.Group>

          <div className="mt-3">
            <button type="submit" className="btn btn-sm pharmacy-info-btn me-2">
              {submitting ? "Submitting..." : "Submit Refund"}
            </button>
            <button
              type="button"
              className="btn btn-sm pharmacy-gray-btn"
              onClick={() => setRefundModalOpen(false)}
            >
              Cancel
            </button>
          </div>
        </form>
      </CustomDialog>



      {/* --- Voucher Print Dialog --- */}
      <CustomDialog
        open={voucherDialogOpen}
        handleClose={() => setVoucherDialogOpen(false)}
        title="Payment Voucher"
        maxWidth="lg"
        isOverflow={false}
        handlePrint={handlePrint}
      >
        <div ref={componentRef} style={{ padding: "16px 30px" }}>
          <style>{fontSize()}</style>
          {voucherData && (
            // <PaymentVoucherReceipt
            //   voucher_no={voucherData.voucher_id}
            //   actions={actions}
            //   entity_type="Supplier Payment"
            // />
            <PaymentVoucherReceipt
              voucher_no={voucherData.voucher_id}
              actions={actions}
              entity_type={voucherData?.voucher_type || "Supplier Refund"}
            />

          )}
        </div>
      </CustomDialog>

      {/* --- Invoices Table --- */}
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table className={classes.table} size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Chalan No</TableCell>
              <TableCell>Total Bill</TableCell>
              <TableCell>Total Paid</TableCell>
              <TableCell>Balance</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Days Left</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <img src="/images/listPreloader.gif" alt="Loading..." width={50} />
                </TableCell>
              </TableRow>
            ) : invoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No outstanding invoices found
                </TableCell>
              </TableRow>
            ) : (
              invoices.map((inv, index) => (
                <TableRow key={index}>
                  <TableCell>{inv.purchase_date ? formatDate(inv.purchase_date) : "-"}</TableCell>
                  <TableCell>{inv.chalan_no}</TableCell>
                  <TableCell>{formatAmount(inv.total_bill)}</TableCell>
                  <TableCell>{formatAmount(inv.total_paid)}</TableCell>
                  <TableCell>{formatAmount(inv.balance)}</TableCell>
                  <TableCell
                    className={
                      inv.status === "cleared"
                        ? classes.cleared
                        : inv.status === "due_today"
                        ? classes.dueToday
                        : classes.overdue
                    }
                  >
                    {inv.status}
                  </TableCell>
                  <TableCell>{inv.days_left}</TableCell>
                  <TableCell>
                    {/* <Tooltip title="View Invoice Details" arrow> */}
                    <Link
                        to={`/single_supplier_details/${inv.supplier_id}/${supplierName}/${inv.supplier_payment_id}`}
                        className={`${classes.actionBtn} ${classes.btnView}`}
                        >
                        View
                        </Link>

                        <button
                        className={`${classes.actionBtn} ${classes.btnReceive}`}
                        onClick={() => handleReceivePayment(inv)}
                        >
                         Make Payments
                        </button>

                        {/* <button
                        className={`${classes.actionBtn} ${classes.btnRefund}`}
                        onClick={() => console.log("Refund for invoice:", inv.related_purchase)}
                        >
                        Refund
                        </button> */}
                        <button
                            className={`${classes.actionBtn} ${classes.btnRefund}`}
                            onClick={() => handleRefund(inv)}
                          >
                            Refund
                          </button>


                    {/* </Tooltip> */}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalCount > perPage && (
        <CustomPagination
          records={perPage}
          handleChangeNextPage={handlePageChange}
          data={invoices}
          total_count={totalCount}
          activePage={page}
        />
      )}
    </div>
  );
};

export default OutStandingInvoices;
